import { useMemo } from 'react';
import {
  LocationFeatureFlagSettingName,
  LocationFeatureSettings,
} from '@weave/schema-gen-ts/dist/schemas/payments/messages/location_settings/location_settings.pb';
import { DataSource } from '@weave/schema-gen-ts/dist/schemas/payments/views/service/service.pb';
import { PaymentsView1 } from '@frontend/api-payments-view';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { PaymentsFeatureFlags, PaymentsFeatureSettings } from '../../types/payment-hooks.types';
import { useLocationFeatureSettingsDirect } from '../locations/use-location-feature-settings';

/*
ORDER IS IMPORTANT.
IF YOU MOVE THE ORDER OF THE paymentsPmsDisplayNamePatterns, IT WILL AFFECT THE BEHAVIOUR OF THE CODE.

TODO:: Robust solution is to don't use the ragex for matching the pms name.
Instead BE schema will return the type as enum and we can use that to match the pms name.
*/
const paymentsPmsDisplayNamePatterns = {
  openDental: /\bOPENDENTAL\b/i,
  openDentalLocal: /OPENDENTAL_CLOUD_LOCAL/i,
  openDentalCloud: /OPENDENTAL_CLOUD/i,
  eaglesoft: /EAGLESOFT(_\d+)?/i,
  fuse: /PATTERSON_FUSE/i,
  drChrono: /DR_CHRONO/i,
  dentrixEnterprise: /DENTRIX_ENTERPRISE/i,
  dolphinCloud: /DOLPHIN_CLOUD/i,
  dolphinLegacy: /DOLPHIN_LEGACY/i,
  dolphin: /DOLPHIN/i,
  crystal: /CRYSTAL/i,
  athenaOne: /ATHENAONE/i,
  nextechPractice: /NEXTECH_PRACTICE/i,
  nextechSelect: /NEXTECH/i,
} as const;

type DisplayNameAccessor = keyof typeof paymentsPmsDisplayNamePatterns;

export const sourceAndFeatureSettingsPmsKeyNameMapping = {
  openDental: 'openDentalWritebacksEnabled',
  openDentalCloud: 'openDentalCloudWritebacksEnabled',
  eaglesoft: 'eaglesoftWritebacksEnabled',
  fuse: 'fuseWritebacksEnabled',
  drChrono: 'drChronoWritebacksEnabled',
  dentrixEnterprise: 'dentrixEnterpriseWritebacksEnabled',
  dolphinCloud: 'dolphinWritebacksEnabled',
  openDentalLocal: 'openDentalLocalWritebacksEnabled',
  dolphin: 'dolphinWritebacksEnabled',
  crystal: 'crystalWritebacksEnabled',
  athenaOne: 'athenaOneWritebacksEnabled',
  nextechSelect: 'nextechSelectWritebacksEnabled',
  nextechPractice: 'nextechPracticeWritebacksEnabled',
  dolphinLegacy: 'dolphinLegacyWritebacksEnabled',
} as const satisfies Partial<Record<DisplayNameAccessor, keyof LocationFeatureSettings>>;

export const pmsTypeToFeatureSettingName = {
  openDental: LocationFeatureFlagSettingName.OPEN_DENTAL_WRITEBACKS_ENABLED,
  openDentalLocal: LocationFeatureFlagSettingName.OPEN_DENTAL_LOCAL_WRITEBACKS_ENABLED,
  openDentalCloud: LocationFeatureFlagSettingName.OPEN_DENTAL_CLOUD_WRITEBACKS_ENABLED,
  eaglesoft: LocationFeatureFlagSettingName.EAGLESOFT_WRITEBACKS_ENABLED,
  fuse: LocationFeatureFlagSettingName.FUSE_WRITEBACKS_ENABLED,
  drChrono: LocationFeatureFlagSettingName.DR_CHRONO_WRITEBACKS_ENABLED,
  dentrixEnterprise: LocationFeatureFlagSettingName.DENTRIX_ENTERPRISE_WRITEBACKS_ENABLED,
  dolphinCloud: LocationFeatureFlagSettingName.DOLPHIN_CLOUD_WRITEBACKS_ENABLED,
  dolphinLegacy: LocationFeatureFlagSettingName.DOLPHIN_LEGACY_WRITEBACKS_ENABLED,
  dolphin: LocationFeatureFlagSettingName.DOLPHIN_WRITEBACKS_ENABLED,
  crystal: LocationFeatureFlagSettingName.CRYSTAL_WRITEBACKS_ENABLED,
  athenaOne: LocationFeatureFlagSettingName.ATHENA_ONE_WRITEBACKS_ENABLED,
  nextechPractice: LocationFeatureFlagSettingName.NEXTECH_PRACTICE_WRITEBACKS_ENABLED,
  nextechSelect: LocationFeatureFlagSettingName.NEXTECH_SELECT_WRITEBACKS_ENABLED,
} as const satisfies Partial<Record<DisplayNameAccessor, LocationFeatureFlagSettingName>>;

export const getFeatureSettingNameFromPmsName = (pmsName: string): LocationFeatureFlagSettingName => {
  const pmsType = findPmsTypeFromName(pmsName);
  if (!pmsType) return LocationFeatureFlagSettingName.UNSPECIFIED;
  return pmsTypeToFeatureSettingName[pmsType as keyof typeof pmsTypeToFeatureSettingName];
};

export const findPmsTypeFromName = (pmsName: string): DisplayNameAccessor | null => {
  for (const [key, pattern] of Object.entries(paymentsPmsDisplayNamePatterns)) {
    if (pattern.test(pmsName)) {
      return key as DisplayNameAccessor;
    }
  }
  return null;
};

function isDefaultUTCDate(utcDate: string) {
  const defaultDate = /^0001-01-01 00:00:00\.?\d* ?[+|-]?\d* ?.*$/;
  const alternateDefaultDate = /^0001-01-01T00:00:00Z$/;
  return defaultDate.test(utcDate) || alternateDefaultDate.test(utcDate);
}

const isSyncCompleted = (source: DataSource): boolean => {
  return !!(
    source?.lastSyncStartedAt &&
    !isDefaultUTCDate(source.lastSyncStartedAt) &&
    source?.lastSyncCompletedAt &&
    !isDefaultUTCDate(source.lastSyncCompletedAt)
  );
};

export const useBulkCollectionsFeature = () => {
  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const enableCollectionFlow = getFeatureFlagValue(PaymentsFeatureFlags.DxeCollectionFlow);

  const { data: sources } = PaymentsView1.Queries.useGetLocationsSourceInfoQuery({
    request: { locationIds: selectedLocationIds },
    options: { enabled: selectedLocationIds.length > 0 && enableCollectionFlow },
  });

  const bulkCollectionsInfo = useMemo(() => {
    const defaultResult = {
      pmsName: null as string | null,
      isBulkCollectionsEligible: false,
    };

    if (!enableCollectionFlow || !sources?.locationsSourceInfo) {
      return defaultResult;
    }

    for (const { locationId, sources: locationSources } of sources.locationsSourceInfo) {
      if (!locationId || !locationSources) continue;

      for (const source of locationSources) {
        if (!isSyncCompleted(source) || !source?.pmsName) continue;

        const pmsType = findPmsTypeFromName(source.pmsName);
        if (!pmsType) continue;

        const isBulkCollectionsEligible = !!source?.capability?.bulkCollectionEnabled;

        if (!isBulkCollectionsEligible) continue;

        return {
          pmsName: source.pmsName,
          isBulkCollectionsEligible,
        };
      }
    }

    return defaultResult;
  }, [sources, enableCollectionFlow]);

  const settingName = getFeatureSettingNameFromPmsName(bulkCollectionsInfo.pmsName ?? '');
  const hasValidSettingName = settingName !== LocationFeatureFlagSettingName.UNSPECIFIED;

  const { enabled } = useLocationFeatureSettingsDirect(settingName as PaymentsFeatureSettings, {
    locationIds: hasValidSettingName ? selectedLocationIds : [], //  it doesn't have an enabled property. The simplest way is to pass an empty array for locationIds when there's no valid setting name, which will prevent any queries from running
  });

  return {
    enableCollectionFlow: bulkCollectionsInfo.isBulkCollectionsEligible && hasValidSettingName && enabled, // is true when PMS is eligible and feature setting is enabled.
    pmsName: bulkCollectionsInfo.pmsName,
    featureSettingEnabled: enabled,
    isBulkCollectionsEligible: bulkCollectionsInfo.isBulkCollectionsEligible, // is true when PMS is eligible regardless of feature setting.
  };
};
